import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
    Calendar, Clock, MapPin, User, CheckCircle, XCircle, 
    PoundSterling, AlertTriangle, Play, ChevronRight, Briefcase,
    Star, Send, MessageSquare, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import TrialFeedbackForm from '../components/TrialFeedbackForm';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';

const TrialManagement = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [trials, setTrials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');
    const [selectedTrial, setSelectedTrial] = useState(null);
    const [feedbackTrial, setFeedbackTrial] = useState(null);
    const [terminateTarget, setTerminateTarget] = useState(null);
    const [terminating, setTerminating] = useState(false);

    useEffect(() => {
        loadTrials();
    }, []);

    const loadTrials = async () => {
        try {
            const res = await api.get('/trials/my-bookings');
            const bookings = res.data?.bookings;
            // Treat null / undefined as an empty list — not an error
            setTrials(Array.isArray(bookings) ? bookings : []);
        } catch (error) {
            // A 404 means the user has no bookings yet — show empty state, not an error
            if (error.response?.status === 404) {
                setTrials([]);
            } else {
                toast.error('Failed to load trials');
            }
        } finally {
            setLoading(false);
        }
    };

    const startTrial = async (trialId) => {
        try {
            await api.post(`/trials/start?trial_id=${trialId}`);
            toast.success('Trial started!');
            loadTrials();
        } catch (error) {
            toast.error(error.userMessage || 'Failed to start trial');
        }
    };

    const completeDay = async (trialId, dayNumber) => {
        try {
            await api.post('/trials/complete-day', {
                trial_id: trialId,
                day_number: dayNumber
            });
            toast.success(`Day ${dayNumber} marked as complete!`);
            loadTrials();
        } catch (error) {
            toast.error(error.userMessage || 'Failed to complete day');
        }
    };

    const terminateTrial = async () => {
        if (!terminateTarget) return;
        setTerminating(true);
        try {
            await api.post('/trials/terminate', {
                trial_id: terminateTarget.id,
                reason: terminateTarget.reason || 'Employer terminated early'
            });
            toast.success('Trial terminated. Refund will be processed.');
            setSelectedTrial(null);
            loadTrials();
        } catch (error) {
            toast.error(error.userMessage || 'Failed to terminate trial');
        } finally {
            setTerminating(false);
            setTerminateTarget(null);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            'pending_candidate_response': { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Awaiting Response' },
            'pending_payment': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending Payment' },
            'payment_failed': { bg: 'bg-red-100', text: 'text-red-800', label: 'Payment Failed' },
            'scheduled': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Scheduled' },
            'in_progress': { bg: 'bg-green-100', text: 'text-green-800', label: 'In Progress' },
            'completed': { bg: 'bg-teal-100', text: 'text-teal-800', label: 'Completed' },
            'terminated_early': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Terminated Early' },
            'cancelled': { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' },
            'cancelled_late': { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled (Late)' },
            'cancelled_by_employer': { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled by Employer' },
            'candidate_no_show': { bg: 'bg-red-100', text: 'text-red-800', label: 'No Show' },
            'expired': { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Expired' },
        };
        // Fall back to a humanised version of the raw status rather than a wrong/misleading badge
        const badge = badges[status] || { bg: 'bg-slate-100', text: 'text-slate-600', label: (status || '').replace(/_/g, ' ') };
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                {badge.label}
            </span>
        );
    };

    const filteredTrials = trials.filter(trial => {
        if (activeTab === 'all') return true;
        if (activeTab === 'active') return ['pending_candidate_response', 'pending_payment', 'payment_failed', 'scheduled', 'in_progress'].includes(trial.status);
        if (activeTab === 'completed') return trial.status === 'completed';
        if (activeTab === 'cancelled') return ['terminated_early', 'cancelled', 'cancelled_late', 'cancelled_by_employer', 'candidate_no_show', 'expired'].includes(trial.status);
        return true;
    });

    const isCompany = user?.user_type === 'company';

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            {/* Header */}
            <div className="sticky top-0 z-50 bg-[#F0FDF4]/80 backdrop-blur-xl border-b border-purple-200/50 shadow-sm">
                <div className="max-w-6xl mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {isCompany && (
                                <button
                                    onClick={() => navigate('/company/dashboard')}
                                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#7C3AED] transition-colors"
                                    data-testid="back-to-dashboard-btn"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                    Dashboard
                                </button>
                            )}
                            <div>
                                <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                    Trial Management
                                </span>
                                <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                                    {isCompany ? 'Trial Shifts' : 'My Trials'}
                                </h1>
                                <p className="text-slate-500 mt-1" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                    {isCompany ? 'Manage your booked trial shifts' : 'View and track your trial shifts'}
                                </p>
                            </div>
                        </div>
                        {isCompany && (
                            <Button 
                                onClick={() => navigate('/company/dashboard')}
                                className="btn-primary"
                            >
                                <Briefcase className="mr-2 h-4 w-4" />
                                View Candidates
                            </Button>
                        )}
                    </div>

                    {/* Tabs */}
                    <div className="flex space-x-2 mt-6">
                        {['all', 'active', 'completed', 'cancelled'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-4 py-2 rounded-full font-medium capitalize text-sm transition-all duration-200 ${
                                    activeTab === tab
                                        ? 'text-white shadow-sm'
                                        : 'border border-purple-200/50 text-slate-600 hover:border-[#8B5CF6] hover:bg-purple-50'
                                }`}
                                style={activeTab === tab ? { background: 'linear-gradient(135deg, #9333EA, #7C3AED)' } : {}}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-6xl mx-auto px-6 py-8">
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto" />
                        <p className="text-gray-500 mt-4">Loading trials...</p>
                    </div>
                ) : filteredTrials.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl border border-purple-200/50 shadow-sm">
                        <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>No trials found</h3>
                        <p className="text-slate-500 mt-1" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            {isCompany 
                                ? 'Book a trial shift from the candidate view' 
                                : 'You haven\'t been booked for any trials yet'}
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-6">
                        {filteredTrials.map((trial) => (
                            <div 
                                key={trial.id}
                                className="bg-white rounded-2xl border border-purple-200/50 shadow-sm overflow-hidden hover:shadow-md transition-all duration-200"
                            >
                                <div className="p-6">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                                    {isCompany ? trial.candidate_name : trial.job_title}
                                                </h3>
                                                {getStatusBadge(trial.status)}
                                            </div>
                                            
                                            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                                <span className="flex items-center">
                                                    <Calendar className="h-4 w-4 mr-1" />
                                                    {new Date(trial.start_date).toLocaleDateString('en-GB', {
                                                        weekday: 'short',
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </span>
                                                {trial.start_time && (
                                                    <span className="flex items-center">
                                                        <Clock className="h-4 w-4 mr-1" />
                                                        {trial.start_time} (UK time)
                                                    </span>
                                                )}
                                                <span className="flex items-center">
                                                    <Clock className="h-4 w-4 mr-1" />
                                                    {trial.trial_days} day{trial.trial_days > 1 ? 's' : ''}
                                                </span>
                                                <span className="flex items-center">
                                                    <MapPin className="h-4 w-4 mr-1" />
                                                    {trial.location}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="text-right">
                                            <p className="text-2xl font-bold text-gray-900">
                                                £{(isCompany ? trial.cost_breakdown?.total : trial.cost_breakdown?.candidate_pay_total)?.toFixed(2) || '0.00'}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {isCompany ? 'Secure Trial Payment' : 'Your Trial Pay'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Progress Bar - For in_progress or completed trials */}
                                    {['in_progress', 'completed', 'terminated_early'].includes(trial.status) && (
                                        <div className="mt-6">
                                            <div className="mb-2">
                                                <span className="text-sm font-medium text-gray-700">
                                                    Progress: {trial.days_completed || 0} of {trial.trial_days} days
                                                </span>
                                            </div>
                                            <div className="flex space-x-2">
                                                {Array.from({ length: trial.trial_days }, (_, i) => {
                                                    const dayNum = i + 1;
                                                    const isCompleted = dayNum <= (trial.days_completed || 0);
                                                    const isNext = dayNum === (trial.days_completed || 0) + 1;
                                                    
                                                    return (
                                                        <div
                                                            key={dayNum}
                                                            className={`
                                                                flex-1 h-10 rounded-lg flex items-center justify-center text-sm font-medium cursor-pointer transition-all
                                                                ${isCompleted 
                                                                    ? 'bg-teal-500 text-white' 
                                                                    : isNext && trial.status === 'in_progress'
                                                                    ? 'bg-purple-100 text-purple-700 border-2 border-purple-300 hover:bg-purple-200'
                                                                    : 'bg-gray-100 text-gray-400'}
                                                            `}
                                                            onClick={() => {
                                                                if (isNext && trial.status === 'in_progress' && isCompany) {
                                                                    completeDay(trial.id, dayNum);
                                                                }
                                                            }}
                                                        >
                                                            {isCompleted ? (
                                                                <CheckCircle className="h-5 w-5" />
                                                            ) : (
                                                                `Day ${dayNum}`
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {trial.status === 'in_progress' && isCompany && (
                                                <p className="text-xs text-purple-600 mt-2">
                                                    Click on the next day to mark it as complete
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="mt-6 flex items-center justify-between">
                                        <div className="flex space-x-2">
                                            {trial.status === 'scheduled' && isCompany && (
                                                <Button
                                                    onClick={() => startTrial(trial.id)}
                                                    className="bg-green-600 hover:bg-green-700"
                                                >
                                                    <Play className="mr-2 h-4 w-4" />
                                                    Start Trial
                                                </Button>
                                            )}
                                            
                                            {trial.status === 'in_progress' && isCompany && (
                                                <Button
                                                    variant="outline"
                                                    onClick={() => setTerminateTarget({ id: trial.id, reason: 'Employer decision' })}
                                                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                                                    data-testid={`end-trial-early-btn-${trial.id}`}
                                                >
                                                    <XCircle className="mr-2 h-4 w-4" />
                                                    End Trial Early
                                                </Button>
                                            )}

                                            {trial.status === 'completed' && isCompany && !trial.has_feedback && (
                                                <Button
                                                    onClick={() => setFeedbackTrial(trial)}
                                                    className="bg-purple-600 hover:bg-purple-700"
                                                    data-testid={`submit-feedback-btn-${trial.id}`}
                                                >
                                                    <Star className="mr-2 h-4 w-4" />
                                                    Submit Feedback
                                                </Button>
                                            )}

                                            {trial.status === 'completed' && isCompany && trial.has_feedback && (
                                                <span className="flex items-center text-sm text-green-600 font-medium">
                                                    <CheckCircle className="mr-1 h-4 w-4" /> Feedback Submitted
                                                </span>
                                            )}
                                        </div>

                                        <Button
                                            variant="ghost"
                                            onClick={() => setSelectedTrial(selectedTrial?.id === trial.id ? null : trial)}
                                            className="text-gray-600"
                                        >
                                            {selectedTrial?.id === trial.id ? 'Hide Details' : 'View Details'}
                                            <ChevronRight className={`ml-1 h-4 w-4 transition-transform ${selectedTrial?.id === trial.id ? 'rotate-90' : ''}`} />
                                        </Button>
                                    </div>

                                    {/* Expanded Details */}
                                    {selectedTrial?.id === trial.id && (
                                        <div className="mt-6 pt-6 border-t bg-gray-50 -mx-6 -mb-6 px-6 pb-6">
                                            <h4 className="font-medium text-gray-900 mb-3">Payment Breakdown</h4>
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                {isCompany && (
                                                    <div>
                                                        <span className="text-gray-500">Platform Fee</span>
                                                        <p className="font-medium">£{trial.cost_breakdown?.platform_fee_total?.toFixed(2)}</p>
                                                    </div>
                                                )}
                                                <div>
                                                    <span className="text-gray-500">{isCompany ? 'Candidate Pay' : 'Your Pay'}</span>
                                                    <p className="font-medium">£{trial.cost_breakdown?.candidate_pay_total?.toFixed(2)}</p>
                                                </div>
                                                {isCompany && trial.refund_breakdown && (
                                                    <>
                                                        <div className="col-span-2 pt-3 border-t mt-2">
                                                            <span className="text-orange-600 font-medium">Refund Details</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">Days Unused</span>
                                                            <p className="font-medium">{trial.refund_breakdown.days_unused}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">Refund Amount</span>
                                                            <p className="font-medium text-green-600">£{trial.refund_breakdown.total_refund?.toFixed(2)}</p>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            
            {/* Feedback Modal */}
            <TrialFeedbackForm
                isOpen={!!feedbackTrial}
                onClose={() => setFeedbackTrial(null)}
                trial={feedbackTrial}
                onComplete={() => loadTrials()}
            />

            {/* Terminate Trial Confirmation */}
            <AlertDialog open={!!terminateTarget} onOpenChange={(open) => { if (!open) setTerminateTarget(null); }}>
                <AlertDialogContent data-testid="terminate-trial-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>End trial early?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to terminate this trial early? A partial refund will be processed for the unused trial days.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={terminating} data-testid="terminate-cancel-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={terminateTrial}
                            disabled={terminating}
                            className="bg-orange-600 hover:bg-orange-700"
                            data-testid="terminate-confirm-btn"
                        >
                            {terminating ? 'Terminating…' : 'End Trial'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default TrialManagement;
